// Generic local-calendar date maths — no booking/staff/finance domain knowledge.
// Extracted from components/bookings/format.ts (originally carried from
// _prototype/bookings.jsx).
//
// Dates are handled as LOCAL calendar days throughout — never via
// toISOString(), which re-serialises in UTC and shifts the day west of
// Greenwich (IST = UTC+1). Date-only strings parse with local midnight and
// format by string assembly, so a 2026-05-20 value is the 20th regardless of
// timezone.

// THE calendar label constants. Every month-abbreviation or day-of-week array
// in the app reads from here — one source, indexed by getMonth() / getDay().
export const MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;
export const DOW_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

// The DATE HALF of a value that may be a full timestamp. The calendar API
// returns a plain `YYYY-MM-DD` for some fields and a full ISO timestamp for
// others, and several helpers here append a time — which on the second kind
// produces `...T09:00:00ZT00:00:00`, an invalid date that printed as
// "undefined" on screen. Every caller that is about to hand a date to one of
// those helpers normalises through this rather than writing `.slice(0, 10)`
// again; it had been written out by hand in three places.
export function dateOnly(iso: string): string {
  return iso.slice(0, 10);
}

export function parseLocal(d: string | number | Date): Date {
  if (d instanceof Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  if (typeof d === 'string') {
    const datePart = d.slice(0, 10);
    const [y = 1970, m, day] = datePart.split('-').map(Number);
    return new Date(y, (m ?? 1) - 1, day ?? 1);
  }
  const x = new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
}

export function startOfDay(d: string | number | Date): Date {
  return parseLocal(d);
}

export function addDays(d: string | number | Date, n: number): Date {
  const x = parseLocal(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function isoDate(d: string | number | Date): string {
  const x = parseLocal(d);
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const dd = String(x.getDate()).padStart(2, '0');
  return `${x.getFullYear()}-${mm}-${dd}`;
}

export function startOfMonth(d: string | number | Date): Date {
  const x = parseLocal(d);
  x.setDate(1);
  return x;
}

export function endOfMonth(d: string | number | Date): Date {
  const x = startOfMonth(d);
  x.setMonth(x.getMonth() + 1);
  x.setDate(0);
  return x;
}

// Month-range helpers (prototype ChartsScopeBar): a range runs from the first
// day of the "from" month to the last day of the "to" month. m is a 0-based
// month index.
export function monthFirstIso(y: number, m: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-01`;
}
export function monthLastIso(y: number, m: number): string {
  const last = new Date(y, m + 1, 0).getDate();
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

// Step a (year, 0-based month) by delta months, normalising the overflow.
export function addMonth(y: number, m: number, delta: number): [number, number] {
  const total = y * 12 + m + delta;
  return [Math.floor(total / 12), ((total % 12) + 12) % 12];
}

// UTC-midnight epoch for an ISO date — day arithmetic that can't pick up a
// DST fraction. Internal to the day-span helpers below.
function utcOf(iso: string): number {
  const p = iso.split('-').map(Number);
  return Date.UTC(p[0] ?? 0, (p[1] ?? 1) - 1, p[2] ?? 1);
}

// Whole days from `fromIso` to `toIso`, end-exclusive — a booking's "nights".
export function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((utcOf(toIso) - utcOf(fromIso)) / 86400000);
}

// Days counted inclusive of BOTH endpoints (a leave span): 12→16 Jun = 5 days,
// same-day = 1. Never negative.
export function daysInclusive(fromIso: string, toIso: string): number {
  return Math.max(0, daysBetween(fromIso, toIso) + 1);
}

// "12 Jun 24" — short day-month-year for rail timelines. Tolerant of a full ISO
// timestamp (slices to the date part).
export function shortDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${+d!} ${MONTHS_ABBR[+m! - 1]} ${y!.slice(2)}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// "Monday 29 June" — full weekday + day + full month, no year. THE date idiom
// for staff messages (the Dogs-Leaving message and the On-duty group message
// both read it, so the two can never disagree about what today is called).
// Parsed as a LOCAL calendar day (parseLocal) so a date never shifts west of
// Greenwich the way toISOString() would (IST = UTC+1).
export function messageDateLabel(dateIso: string): string {
  const d = parseLocal(dateIso);
  return `${WEEKDAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

export function fmtDate(d: string | number | Date | null | undefined): string {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function fmtDateRange(checkIn: string, checkOut: string): string {
  return `${fmtDate(checkIn)} → ${fmtDate(checkOut)}`;
}
